import { TEN_MINUTES, THREE_MINUTES } from '@/constants';

/**
 * Rounds a timestamp to the nearest 5 seconds.
 *
 * @param {number} timestamp - The timestamp in seconds.
 * @returns {number} - The timestamp rounded to the nearest 5 seconds.
 */
export function roundSecondsToFiveSeconds(timestamp: number): number {
  return Math.round(timestamp / 5) * 5;
}

/**
 * Rounds a timestamp to the nearest minute.
 *
 * @param {number} timestamp - The timestamp in seconds.
 * @returns {number} - The timestamp rounded to the nearest minute.
 */
export function roundSecondsToNearestMinutes(timestamp: number): number {
  return Math.round(timestamp / 60) * 60;
}

/**
 * Formats a duration for analytics.
 *
 * This function takes a duration in milliseconds, rounds it to the nearest 5 seconds,
 * and then returns a string representation of the duration.
 *
 * If the duration is more than 10 minutes (600 seconds), it returns '>10min'.
 * If the duration is more than 3 minutes (180 seconds), it rounds the duration to the nearest minute.
 * Otherwise, it returns the duration in seconds.
 *
 * @param {number} durationInMs - The duration in milliseconds.
 * @returns {string} - The formatted duration.
 */
export function formatDurationForAnalytics(durationInMs: number): string {
  const seconds = roundSecondsToFiveSeconds(durationInMs / 1000);

  if (seconds > TEN_MINUTES) {
    return '>10m';
  }

  if (seconds > THREE_MINUTES) {
    return `${roundSecondsToNearestMinutes(seconds)}s`;
  }

  return `${seconds}s`;
}
